import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Trash2, Pencil, Heart, Cake } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import RelationshipDialog from "@/components/RelationshipDialog";

export default function Relationships() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/relationships${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(res.data);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing(null);
      setShowDialog(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const remove = async (id) => {
    if (!confirm("Delete this relationship? Celebration history will also be removed.")) return;
    await api.delete(`/relationships/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="relationships-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl text-charcoal">People</h1>
          <p className="text-charcoal/60 mt-2">Everyone worth celebrating.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowDialog(true); }} className="pill-btn pill-btn-primary flex items-center gap-2" data-testid="add-relationship-btn">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Add person
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-charcoal/40 absolute left-4 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
          data-testid="relationships-search"
          className="w-full rounded-full bg-white border border-warm pl-11 pr-4 py-3 focus:outline-none focus:border-terracotta" />
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => <div key={i} className="card-surface p-6 h-32 animate-pulse bg-linen-2" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="relationships-empty">
          <p className="font-display text-2xl text-charcoal mb-2">No one yet</p>
          <p className="text-charcoal/60 mb-6">Add someone important to start remembering.</p>
          <button onClick={() => { setEditing(null); setShowDialog(true); }} className="pill-btn pill-btn-primary" data-testid="empty-add-person">Add someone</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((r, i) => {
            const nextDays = Math.min(...[r.birthday_days_left, r.anniversary_days_left].filter((x) => x != null));
            return (
              <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                data-testid={`relationship-card-${i}`}
                className="card-surface p-6 cursor-pointer" onClick={() => navigate(`/relationships/${r.id}`)}>
                <div className="flex items-start gap-4">
                  {r.picture ? (
                    <img src={r.picture} className="w-14 h-14 rounded-full object-cover border border-warm" alt="" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center font-display text-lg text-charcoal">
                      {r.name.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-xl text-charcoal truncate">{r.name}</p>
                    <p className="text-sm text-charcoal/60">{r.relationship_type}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {r.fav_color && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-linen text-charcoal border border-warm">{r.fav_color}</span>}
                      {r.fav_flower && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-linen text-charcoal border border-warm">{r.fav_flower}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setEditing(r); setShowDialog(true); }}
                      className="p-1.5 rounded-full hover:bg-linen" data-testid={`edit-rel-${i}`}>
                      <Pencil className="w-4 h-4 text-charcoal/60" strokeWidth={1.5} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                      className="p-1.5 rounded-full hover:bg-linen" data-testid={`delete-rel-${i}`}>
                      <Trash2 className="w-4 h-4 text-charcoal/60" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-warm flex items-center justify-between text-sm text-charcoal/70">
                  {r.birthday && (<span className="flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" strokeWidth={1.5} />{r.birthday}</span>)}
                  {r.anniversary && (<span className="flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" strokeWidth={1.5} />{r.anniversary}</span>)}
                  {isFinite(nextDays) && <span className="text-terracotta font-semibold">{nextDays}d</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {showDialog && (
        <RelationshipDialog
          initial={editing}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}
